import primitives from '@veneer/primitives'
import { extendSemantics } from '@veneer/semantics'
import { ThemeProvider as VeneerThemeProvider } from '@veneer/theme'
import React from 'react'
import { createGlobalStyle } from 'styled-components'
import { useSettings, useSignupSession, useSignupState } from '../../../hooks'
import { resolveThemeMode } from '../../../lib/themeMode'
import { hpxTokens } from './hpxTokens'
import { SignupTheme, hpxDarkTheme, hpxLightTheme } from './theme'

export const DEFAULT_MODE = 'light'
export const MODE = DEFAULT_MODE
export const DENSITY = 'standard'

export const shape = (isHpx: boolean) => {
  return isHpx ? 'pill' : 'round'
}

export const customSemantics = (isHpx: boolean) => {
  const HpxTheme = extendSemantics({
    color: {
      light: {
        background: {
          disabled: hpxTokens.royalBlue3, // disabled primary background
          default: hpxTokens.background
        },
        foreground: {
          disabled: primitives.color.gray5 // disabled primary text and border, also disabled secondary text and border
          // disabled: hpxTokens.royalBlue4 // gets correct disabled secondary button, but cannot see text as well in disabled primary button
        },
        primary: {
          foreground: {
            default: primitives.color.white // primary text
          },
          base: {
            default: hpxTokens.royalBlue7, // primary background
            hover: hpxTokens.royalBlue8, // primary hover, secondary border and font
            active: hpxTokens.royalBlue9 // primary click
          },
          background: {
            hover: hpxTokens.background, // secondary background hover
            active: hpxTokens.royalBlue3 // secondary background click
          }
        }
      },
      dark: {
        background: {
          disabled: hpxTokens.royalBlue10, // disabled primary background (dark)
          default: hpxTokens.secondaryText // dark page background
        },
        foreground: {
          disabled: primitives.color.gray6 // disabled text in dark mode
        },
        primary: {
          foreground: {
            default: primitives.color.gray12 // dark text on lighter button
          },
          base: {
            default: hpxTokens.royalBlue5, // lighter royal blue for dark background
            hover: hpxTokens.royalBlue4, // lighter hover
            active: hpxTokens.royalBlue3 // lightest active
          },
          background: {
            hover: hpxTokens.secondaryText, // dark background hover
            active: hpxTokens.royalBlue11 // dark active background
          }
        }
      }
    }
  })

  return isHpx ? HpxTheme : extendSemantics({})
}

export const HpxGlobalStyle = createGlobalStyle<{ theme?: SignupTheme }>`
  :root {
    --signup-bg-main: ${({ theme }) => theme.bgMain};
    --signup-checkout-disabled-bg: ${({ theme }) => theme.checkoutDisabledBg};
    --signup-secondary-text: ${({ theme }) => theme.secondaryText};
    --signup-secondary-border: ${({ theme }) => theme.secondaryBorder};
    --signup-royal-blue-3: ${({ theme }) => theme.royalBlue3};
    --signup-royal-blue-7: ${({ theme }) => theme.royalBlue7};
    --signup-divider-color: ${({ theme }) => theme.dividerColor};
    --signup-subtitle-color: ${({ theme }) => theme.subtitleColor};
    --signup-card-bg: ${({ theme }) => theme.cardBg};
    --signup-card-surface: ${({ theme }) => theme.cardSurface};
    --signup-primary-accent-fg: ${({ theme }) => theme.primaryAccentFg};
    --signup-overlay-bg: ${({ theme }) => theme.overlayBg};
    --signup-overlay-bg-strong: ${({ theme }) => theme.overlayBgStrong};
    --signup-error-icon-color: ${({ theme }) => theme.errorIconColor};
  }
`

export const LayoutTheme = ({ children }) => {
  const { isHpx } = useSignupSession()
  const settingsState = useSettings()
  const signupState = useSignupState()

  const enableDarkModeSignup = settingsState.data?.enableDarkModeSignup
  const appMode = signupState.onboarding?.sessionContext?.app?.mode

  const resolvedMode = resolveThemeMode(enableDarkModeSignup, appMode)
  const isStyleguide = !!(globalThis as any).__STYLEGUIDE__
  const [mode, setMode] = React.useState<'light' | 'dark'>(
    () =>
      (isStyleguide &&
        (sessionStorage.getItem('styleguide-theme') as 'light' | 'dark')) ||
      resolvedMode
  )

  React.useEffect(() => {
    if (!isStyleguide) return
    const handler = () => {
      setMode(
        (sessionStorage.getItem('styleguide-theme') as 'light' | 'dark') ||
          resolvedMode
      )
    }
    globalThis.addEventListener('styleguide-theme-change', handler)
    return () =>
      globalThis.removeEventListener('styleguide-theme-change', handler)
  }, [isStyleguide, resolvedMode])

  const signupTheme = mode === 'dark' ? hpxDarkTheme : hpxLightTheme

  return (
    <div className="layouttheme">
      <VeneerThemeProvider
        customSemantics={customSemantics(isHpx)}
        mode={mode}
        shape={shape(isHpx)}
        density={DENSITY}
      >
        {isHpx && <HpxGlobalStyle theme={signupTheme} />}
        {children}
      </VeneerThemeProvider>
    </div>
  )
}
